import { PhoneOutgoing } from 'lucide-react';

import { CopyButton } from '@/components/copy-button';
import { Badge } from '@/components/ui/badge';
import { SortableHeader } from '@/components/ui/data-table';

import { getOutcomeColorClass } from '@/lib/avatarColor';
import { formatDate, humanizeDuration } from '@/lib/date';
import { formatAsE164 } from '@/lib/phone';

import type { Call } from '@/types/call.type';
import type { ColumnDef } from '@tanstack/react-table';

export const getColumns = (): ColumnDef<Call>[] => [
  {
    id: 'id',
    accessorKey: 'id',
    header: 'Call ID',
    cell: ({ getValue }) => <span className="font-medium">{getValue() as number}</span>,
  },
  {
    id: 'target_phone_number',
    accessorKey: 'target_phone_number',
    header: ({ column }) => <SortableHeader label="Phone number" column={column} />,
    cell: ({ getValue }) => (
      <div className="flex items-center">
        <span>{formatAsE164(getValue() as string)}</span>
        <CopyButton
          value={getValue() as string}
          message="Phone number copied to clipboard"
          ariaLabel="Copy phone number"
        />
      </div>
    ),
  },
  {
    id: 'call_mode',
    accessorKey: 'call_mode',
    header: ({ column }) => <SortableHeader label="Call Mode" column={column} />,
    cell: ({ getValue }) => (
      <Badge variant="outline" className="inline-flex items-center gap-2">
        <PhoneOutgoing className="h-4 w-4 text-blue-700 dark:text-blue-400" />
        {getValue() as string}
      </Badge>
    ),
  },
  {
    id: 'call_status',
    accessorKey: 'call_status',
    header: ({ column }) => <SortableHeader label="Outcome" column={column} />,
    cell: ({ getValue }) => {
      const status = getValue() as string;
      return (
        <Badge variant="outline">
          <svg
            viewBox="0 0 6 6"
            aria-hidden={true}
            className={`!w-1.5 !h-1.5 ${getOutcomeColorClass(status)}`}
          >
            <circle r="3" cx="3" cy="3" fill="currentColor" />
          </svg>
          <span className={getOutcomeColorClass(status)}>{status}</span>
        </Badge>
      );
    },
  },
  {
    id: 'start_timestamp',
    accessorKey: 'start_timestamp',
    header: ({ column }) => <SortableHeader label="Start at" column={column} />,
    cell: ({ getValue }) => <span className="text-sm">{formatDate(getValue() as string)}</span>,
  },
  {
    id: 'end_timestamp',
    accessorKey: 'end_timestamp',
    header: ({ column }) => <SortableHeader label="Ended at" column={column} />,
    cell: ({ getValue }) => {
      const val = getValue() as string | undefined;
      return <span className="text-sm">{val ? formatDate(val) : '-'}</span>;
    },
  },
  {
    id: 'call_duration',
    accessorKey: 'call_duration',
    header: ({ column }) => <SortableHeader label="Duration" column={column} />,
    cell: ({ getValue }) => (
      <span className="text-sm">{humanizeDuration(getValue() as number)}</span>
    ),
  },
];
